import './RecordedWebinar.scss'

import classNames from 'classnames'
import PlayCircle from 'icons/PlayCircle'
import SubscribeDialog from 'lib/components/SubscribeDialog/SubscribeDialog'
import AdminContext from 'lib/context/AdminContext'
import AuthContext from 'lib/context/AuthContext'
import { events, track } from 'lib/util/analytics'
import formatTime from 'lib/util/formatTime'
import { QUERY_KEYS } from 'lib/util/routing'
import { preserveScrollAndNavigate } from 'lib/util/scrollBehavior'
import { webinarDetailUrl } from 'lib/util/urls'
import RecordedWebinarType from 'pages/Home/RecordedWebinarType'
import { isPublished } from 'pages/Home/sections/filtering'
import Series from 'pages/Home/Series'
import React, { MouseEvent, useContext, useRef, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'

import ListItemHeader from '../sections/ListItemHeader'
import RecordedWebinarAdminControls from './admin/RecordedWebinarAdminControls'

export interface RecordedWebinarProps {
  recordedWebinar: RecordedWebinarType
  renderSeriesLink?: boolean
  series: Series[]
}

export default function RecordedWebinar(props: RecordedWebinarProps) {
  const { recordedWebinar, renderSeriesLink, series } = props
  const { title, description, more, rise, skillLevel, storyline } = recordedWebinar
  const { showAdminControls } = useContext(AdminContext)
  const { getAccessToken } = useContext(AuthContext)
  const history = useHistory()
  const [isSubscribeDialogOpen, setSubscribeDialogOpen] = useState(false)
  const webinarEl = useRef(null)

  const pathname = webinarDetailUrl(recordedWebinar)

  function onClick(e: MouseEvent) {
    track(
      events.webinarClicked,
      {
        webinarId: recordedWebinar.id,
        title,
        rise,
        storyline,
        more,
        seriesId: recordedWebinar.series.id,
        seriesTitle: recordedWebinar.series.title,
        skillLevel: recordedWebinar.skillLevel
      },
      getAccessToken,
      showAdminControls
    )

    if (e.ctrlKey || e.metaKey || e.shiftKey) {
      // don't interfere with "special" clicks
      return
    }

    e.preventDefault()

    preserveScrollAndNavigate(history, { pathname }, webinarEl)
  }

  const className = classNames('recorded-webinar', { 'recorded-webinar--unpublished': !isPublished(recordedWebinar) })

  return (
    <li className={className} id={`webinar-${recordedWebinar.id}`} ref={webinarEl}>
      <ListItemHeader
        clearQueryKeys={[QUERY_KEYS.sort, QUERY_KEYS.skill]}
        renderSeriesLink={renderSeriesLink}
        series={recordedWebinar.series}
        skillLevel={skillLevel}
      />
      <Link className="recorded-webinar__content" to={{ pathname }} onClick={onClick}>
        <div className="recorded-webinar__detail-section">
          <h3 className="recorded-webinar__title">{title}</h3>
          <p className="recorded-webinar__description">{description}</p>
        </div>
        <div className="recorded-webinar__play-section">
          <button className="recorded-webinar__play-button">
            <PlayCircle />
          </button>
          {recordedWebinar.video && recordedWebinar.video.duration && (
            <span className="recorded-webinar__duration">{formatTime(recordedWebinar.video.duration)}</span>
          )}
        </div>
      </Link>
      {showAdminControls && <RecordedWebinarAdminControls recordedWebinar={recordedWebinar} series={series} />}
      {isSubscribeDialogOpen && <SubscribeDialog onRequestClose={() => setSubscribeDialogOpen(false)} />}
    </li>
  )
}
